import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supplierAPI } from './supplierAPI';
import { Supplier, CreateSupplierData, UpdateSupplierData, SupplierQueryParams, SupplierDropdownItem } from '../../types/supplier';
import { PaginationMeta } from '../../types/pagination';

interface SupplierState {
  suppliers: Supplier[];
  dropdownSuppliers: SupplierDropdownItem[];
  currentSupplier: Supplier | null;
  pagination: PaginationMeta | null;
  loading: boolean;
  error: string | null;
  filters: SupplierQueryParams;
}

const initialState: SupplierState = {
  suppliers: [],
  dropdownSuppliers: [],
  currentSupplier: null,
  pagination: null,
  loading: false,
  error: null,
  filters: {
    page: 1,
    limit: 10,
    search: '',
    status: undefined,
    city: '',
    state: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
};

// Async thunks
export const fetchSuppliers = createAsyncThunk(
  'suppliers/fetchSuppliers',
  async (params: SupplierQueryParams = {}) => {
    const response = await supplierAPI.getSuppliers(params);
    return response;
  }
);

export const fetchSuppliersDropdown = createAsyncThunk(
  'suppliers/fetchSuppliersDropdown',
  async () => {
    const response = await supplierAPI.getSuppliersDropdown();
    return response;
  }
);

export const fetchSupplier = createAsyncThunk(
  'suppliers/fetchSupplier',
  async (uuid: string) => {
    const response = await supplierAPI.getSupplier(uuid);
    return response;
  }
);

export const createSupplier = createAsyncThunk(
  'suppliers/createSupplier',
  async (data: CreateSupplierData, { rejectWithValue }) => {
    try {
      const response = await supplierAPI.createSupplier(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create supplier');
    }
  }
);

export const updateSupplier = createAsyncThunk(
  'suppliers/updateSupplier',
  async ({ uuid, data }: { uuid: string; data: UpdateSupplierData }, { rejectWithValue }) => {
    try {
      const response = await supplierAPI.updateSupplier(uuid, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update supplier');
    }
  }
);

export const deleteSupplier = createAsyncThunk(
  'suppliers/deleteSupplier',
  async (uuid: string, { rejectWithValue }) => {
    try {
      await supplierAPI.deleteSupplier(uuid);
      return uuid;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete supplier');
    }
  }
);

export const restoreSupplier = createAsyncThunk(
  'suppliers/restoreSupplier',
  async (uuid: string, { rejectWithValue }) => {
    try {
      const response = await supplierAPI.restoreSupplier(uuid);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to restore supplier');
    }
  }
);

const supplierSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<SupplierQueryParams>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentSupplier: (state) => {
      state.currentSupplier = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSuppliers: (state, action: PayloadAction<Supplier[]>) => {
      state.suppliers = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch suppliers
      .addCase(fetchSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        console.log('🔍 Supplier Slice - Full Action Payload:', action.payload);
        console.log('🔍 Supplier Slice - Payload Type:', typeof action.payload);
        
        if (action.payload && action.payload.data) {
          console.log('🔍 Supplier Slice - Data:', action.payload.data);
          console.log('🔍 Supplier Slice - Data Type:', typeof action.payload.data);
          console.log('🔍 Supplier Slice - Is Array:', Array.isArray(action.payload.data));
          
          if (Array.isArray(action.payload.data) && action.payload.data.length > 0) {
            console.log('🔍 Supplier Slice - First Item:', action.payload.data[0]);
            console.log('🔍 Supplier Slice - First Item Type:', typeof action.payload.data[0]);
          }
        }
        
        // For now, just use the data as-is and let's see what we actually get
        state.suppliers = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch suppliers';
      })
      
      // Fetch suppliers dropdown
      .addCase(fetchSuppliersDropdown.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSuppliersDropdown.fulfilled, (state, action) => {
        state.loading = false;
        state.dropdownSuppliers = action.payload.data || [];
      })
      .addCase(fetchSuppliersDropdown.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch suppliers dropdown';
      })
      
      // Fetch single supplier
      .addCase(fetchSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplier.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSupplier = action.payload.data;
      })
      .addCase(fetchSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch supplier';
      })
      
      // Create supplier
      .addCase(createSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSupplier.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update supplier
      .addCase(updateSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        state.loading = false;
        const updatedSupplier = action.payload.data;
        const index = state.suppliers.findIndex(s => s.uuid === updatedSupplier.uuid);
        if (index !== -1) {
          state.suppliers[index] = updatedSupplier;
        }
        if (state.currentSupplier?.uuid === updatedSupplier.uuid) {
          state.currentSupplier = updatedSupplier;
        }
      })
      .addCase(updateSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete supplier
      .addCase(deleteSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSupplier.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers = state.suppliers.filter(s => s.uuid !== action.payload);
      })
      .addCase(deleteSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Restore supplier
      .addCase(restoreSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreSupplier.fulfilled, (state, action) => {
        state.loading = false;
        const restoredSupplier = action.payload.data;
        state.suppliers.push(restoredSupplier);
      })
      .addCase(restoreSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, clearCurrentSupplier, clearError, setSuppliers } = supplierSlice.actions;

// Selectors
export const selectSuppliers = (state: { suppliers: SupplierState }) => state.suppliers.suppliers;
export const selectDropdownSuppliers = (state: { suppliers: SupplierState }) => state.suppliers.dropdownSuppliers;
export const selectCurrentSupplier = (state: { suppliers: SupplierState }) => state.suppliers.currentSupplier;
export const selectSupplierPagination = (state: { suppliers: SupplierState }) => state.suppliers.pagination;
export const selectSupplierLoading = (state: { suppliers: SupplierState }) => state.suppliers.loading;
export const selectSupplierError = (state: { suppliers: SupplierState }) => state.suppliers.error;
export const selectSupplierFilters = (state: { suppliers: SupplierState }) => state.suppliers.filters;

export default supplierSlice.reducer;